var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import cl from "clsx";
import { isWeekend } from "date-fns";
import React, { forwardRef, useRef, useState } from "react";
import { DayPicker, isMatch } from "react-day-picker";
import { omit } from "../../util/index.js";
import { useId } from "../../util/hooks/index.js";
import { useMergeRefs } from "../../util/hooks/useMergeRefs.js";
import { DateContext } from "../context/index.js";
import { DatePickerInput } from "../parts/DateInput.js";
import { DateWrapper } from "../parts/DateWrapper.js";
import { getLocaleFromString, labels } from "../utils/index.js";
import DatePickerStandalone from "./DatePickerStandalone.js";
import Caption from "./parts/Caption.js";
import DropdownCaption from "./parts/DropdownCaption.js";
import { HeadRow } from "./parts/HeadRow.js";
import Row from "./parts/Row.js";
import TableHead from "./parts/TableHead.js";
import WeekNumber from "./parts/WeekNumber.js";
/**
 * A component that allows users to select a date from a calendar.
 *
 * @see [📝 Documentation](https://aksel.nav.no/komponenter/core/datepicker)
 * @see 🏷️ {@link DatePickerProps}
 *
 * @example
 * ```jsx
 *  const { inputProps, datepickerProps } = useMonthpicker({
 *    onMonthChange: console.log,
 *  });
 *
 *  return (
 *     <DatePicker {...datepickerProps} dropdownCaption>
 *       <DatePicker.Input
 *         {...inputProps}
 *         label="Velg dato"
 *       />
 *     </DatePicker>
 *  );
 * ```
 */
export const DatePicker = forwardRef((_a, ref) => {
    var _b;
    var { children, locale = "nb", dropdownCaption, disabled = [], disableWeekends = false, showWeekNumber = false, selected, id, defaultSelected, className, wrapperClassName, open: _open, onClose, onOpenToggle, strategy, onWeekNumberClick } = _a, rest = __rest(_a, ["children", "locale", "dropdownCaption", "disabled", "disableWeekends", "showWeekNumber", "selected", "id", "defaultSelected", "className", "wrapperClassName", "open", "onClose", "onOpenToggle", "strategy", "onWeekNumberClick"]);
    const ariaId = useId(id);
    const [open, setOpen] = useState(_open !== null && _open !== void 0 ? _open : false);
    const wrapperRef = useRef(null);
    const mergedRef = useMergeRefs(wrapperRef, ref);
    const [selectedDates, setSelectedDates] = React.useState(defaultSelected);
    const mode = (_b = rest.mode) !== null && _b !== void 0 ? _b : "single";
    /**
     * @param newSelected Date | Date[] | DateRange | undefined
     */
    const handleSelect = (newSelected) => {
        var _a, _b, _c;
        setSelectedDates(newSelected);
        if (rest.mode === "single") {
            newSelected && ((_a = onClose === null || onClose === void 0 ? void 0 : onClose()) !== null && _a !== void 0 ? _a : setOpen(false));
        }
        else if (rest.mode === "range") {
            (newSelected === null || newSelected === void 0 ? void 0 : newSelected.from) && (newSelected === null || newSelected === void 0 ? void 0 : newSelected.to) && ((_b = onClose === null || onClose === void 0 ? void 0 : onClose()) !== null && _b !== void 0 ? _b : setOpen(false));
        }
        (_c = rest === null || rest === void 0 ? void 0 : rest.onSelect) === null || _c === void 0 ? void 0 : _c.call(rest, newSelected);
    };
    const DatePickerComponent = (React.createElement(DayPicker, Object.assign({ locale: getLocaleFromString(locale), mode: mode, onSelect: handleSelect, selected: selected !== null && selected !== void 0 ? selected : selectedDates, components: {
            Caption: dropdownCaption ? DropdownCaption : Caption,
            Head: TableHead,
            HeadRow,
            WeekNumber,
            Row,
        }, className: cl("navds-date", className), classNames: {
            vhidden: "navds-sr-only",
        }, disabled: (day) => {
            return (disableWeekends && isWeekend(day)) || isMatch(day, disabled);
        }, weekStartsOn: 1, initialFocus: false, labels: labels, modifiers: {
            weekend: (day) => disableWeekends && isWeekend(day),
        }, modifiersClassNames: {
            weekend: "rdp-day__weekend",
        }, showWeekNumber: showWeekNumber, onWeekNumberClick: mode === "multiple" ? onWeekNumberClick : undefined, fixedWeeks: true, showOutsideDays: true }, omit(rest, ["onSelect"]))));
    return (React.createElement(DateContext.Provider, { value: {
            open: _open !== null && _open !== void 0 ? _open : open,
            onOpen: () => {
                setOpen((x) => !x);
                onOpenToggle === null || onOpenToggle === void 0 ? void 0 : onOpenToggle();
            },
            ariaId,
            defined: true,
        } },
        React.createElement("div", { ref: mergedRef, className: cl("navds-date__wrapper", wrapperClassName) },
            children,
            React.createElement(DateWrapper, { open: _open !== null && _open !== void 0 ? _open : open, anchor: wrapperRef.current, onClose: () => { var _a; return (_a = onClose === null || onClose === void 0 ? void 0 : onClose()) !== null && _a !== void 0 ? _a : setOpen(false); }, locale: locale, variant: mode, popoverProps: {
                    id: ariaId,
                    strategy,
                } }, DatePickerComponent))));
});
DatePicker.Standalone = DatePickerStandalone;
DatePicker.Input = DatePickerInput;
export default DatePicker;
//# sourceMappingURL=DatePicker.js.map